/**
 * SAVING A WAY TO PAY WITH NOTHING OWED, on How you pay (ops's `docs/seams/home.md`, the revision of
 * 2026-10-17, sections 12.2, 12.5 and 12.7; round five, slice A5). Drawn only where the Worker says
 * the offer is open (`saveOffered`): its var says ops's door for setup pages is open on the host it
 * talks to, nothing is saved, and she is a granted client. No artboard draws it (business issue 13),
 * so it is built from How you pay's drawn parts and the ways to pay's (`StripeWay.tsx`), with ops's
 * words beside each button and home's own around them (`SAVE` in `words.ts`, NEW).
 *
 * - "SAVE A BANK ACCOUNT" AND "SAVE A CARD", each in a card of its own, ops's words for it first,
 *   `AUTOPAY_SETUP_BANK_TERMS` and `AUTOPAY_SETUP_CARD_TERMS`, word for word, the office's number in
 *   them a link to call it (`Terms.tsx`), and the button under them, which the words describe as she
 *   reaches it: the paragraph beside home's button is the opt-in (12.2). The bank before the card,
 *   as ops lists them.
 * - A PRESS GOES BEHIND A FRESH CODE (CLAUDE.md binding rule 5), always: saving a method changes how
 *   the household pays. It sends `billing.autopay_start` with the page and the version of the words
 *   drawn beside the button, `terms_version` (12.2), to the door behind a code, and nothing of where
 *   Stripe sends her back, which the Worker writes: How you pay's own address. With no fresh code
 *   standing as far as her browser knows (`lib/fresh.ts`), no panel is drawn and the button is held
 *   busy while the door is asked, which asks for the code last: it answers what it would refuse the
 *   press with, or `403 step_up_required` only for a press it would take, which draws the code step
 *   in place of the button pressed, so no code is asked for a press it would then refuse (round six,
 *   slice S1). Once the code works the same ask goes again with the same key.
 * - THE PANEL PAYING HAS (`Leaving.tsx`): "One moment" alone until the door has the ask, then that
 *   she is being taken to Stripe to save her bank account or her card, never to pay; the poll every
 *   three seconds, six times at most, which the Worker answers by a replay of the same request, as
 *   for a pay (12.2); once the page is ready the tab goes to it, the panel still up. Ops hands a page
 *   back only to the person it was made for, and the Worker polls only her own ask (12.7).
 * - EVERY END SAYS WHAT TO DO: a method already saved, in ops's words, `ALREADY_SAVED`, and How you
 *   pay read again as she closes the panel, so it draws the method ops has; every setup page used up,
 *   in ops's words with the office's number; the door's own limits; a dropped connection; and
 *   anything else as not gone through, with the phone and Try again.
 * - WORDS THAT HAVE CHANGED (12.2, 12.7): ops's `409 terms_changed` means the words drawn are not
 *   the ones that hold, and the only way to draw those is How you pay as the Worker serves it now,
 *   so the tab notes the kind she pressed on and loads it afresh (`lib/paying.ts`); the page then says
 *   ops's sentence above that kind's words, with the focus, until she presses.
 * - NOTHING IS SAVED HERE. How you pay says a method is saved only once ops pushes it, or Stripe's
 *   way back says it in ops's own words (`AccountPaying.tsx`); this says only where she is going.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { getSetupPage, sendRequest, type AskAnswer, type TooMany } from '../api.ts';
import { freshStands, noFreshCode } from '../lib/fresh.ts';
import {
  away,
  forgetSetupTermsChanged,
  noteSetupTermsChanged,
  setupTermsChangedOn,
  type SetupKind,
} from '../lib/paying.ts';
import {
  ALREADY_SAVED,
  AUTOPAY_SETUP_BANK_TERMS,
  AUTOPAY_SETUP_BANK_TERMS_VERSION,
  AUTOPAY_SETUP_CARD_TERMS,
  AUTOPAY_SETUP_CARD_TERMS_VERSION,
  FAILED_BEFORE_PHONE,
  PAYING,
  SAVE,
  SETUP_PAGES_USED_UP_BEFORE_PHONE,
  TERMS_CHANGED,
  tooManyBeforePhone,
} from '../words.ts';
import { NAVY_BUTTON_WRAPPING } from './buttons.ts';
import { FreshCode, type ProvedCame } from './FreshCode.tsx';
import type { Signed } from './Guarded.tsx';
import { LeavingPanel, pollForPage, useLeaving, type PollEnded } from './Leaving.tsx';
import { Phone } from './Phone.tsx';
import { TermsWords } from './Terms.tsx';

/** The request type, ops's section 12.2. */
const SETUP_TYPE = 'billing.autopay_start';

/** Ops's refusals this screen answers in its own way (12.5, 12.7). */
const TERMS_CHANGED_CODE = 'terms_changed';
const ALREADY_SAVED_CODE = 'already_saved';
const USED_UP_CODE = 'setup_pages_used_up';

/**
 * How the press ended, said in the panel: not gone through, the page still being made, the
 * connection dropped, the door's own limits, a method already saved, or every setup page used up.
 */
type Ended = PollEnded | 'too_many' | 'already_saved' | 'used_up';

/** What each kind draws and sends: its words and their version, and its own lines. */
const KINDS: Readonly<
  Record<
    SetupKind,
    {
      readonly words: string;
      readonly version: string;
      readonly button: string;
      readonly going: string;
      readonly freshCode: string;
      readonly cannotBeforePhone: string;
    }
  >
> = {
  bank: {
    words: AUTOPAY_SETUP_BANK_TERMS,
    version: AUTOPAY_SETUP_BANK_TERMS_VERSION,
    button: SAVE.bank,
    going: SAVE.takingYouBank,
    freshCode: SAVE.bankFreshCode,
    cannotBeforePhone: SAVE.bankCannotBeforePhone,
  },
  card: {
    words: AUTOPAY_SETUP_CARD_TERMS,
    version: AUTOPAY_SETUP_CARD_TERMS_VERSION,
    button: SAVE.card,
    going: SAVE.takingYouCard,
    freshCode: SAVE.cardFreshCode,
    cannotBeforePhone: SAVE.cardCannotBeforePhone,
  },
};

const ORDER: readonly SetupKind[] = ['bank', 'card'];

/**
 * What the panel says of how a press ended; past the door's own limits, with whose count and when,
 * as the door said them (round six, slice S1).
 */
function endedWords(line: Ended, limit: TooMany | undefined) {
  switch (line) {
    case 'already_saved':
      return ALREADY_SAVED;
    case 'used_up':
      return (
        <>
          {SETUP_PAGES_USED_UP_BEFORE_PHONE} <Phone after="." />
        </>
      );
    case 'too_many':
      return (
        <>
          {tooManyBeforePhone(limit)} <Phone after="." />
        </>
      );
    case 'slow':
      return (
        <>
          {PAYING.slowBeforePhone} <Phone after="." />
        </>
      );
    case 'offline':
      return (
        <>
          {PAYING.connectionDroppedBeforePhone} <Phone after="." />
        </>
      );
    case 'failed':
      return (
        <>
          {FAILED_BEFORE_PHONE} <Phone after="." />
        </>
      );
  }
}

/** The ends after which trying again now would only be refused again. */
const NO_RETRY: ReadonlySet<Ended> = new Set(['too_many', 'already_saved', 'used_up']);

interface SaveMethodProps {
  /** The household shown, which the request names. */
  readonly household: string;
  readonly signed: Signed;
  /** Reads How you pay again. */
  readonly reload: () => void;
}

export function SaveMethod({ household, signed, reload }: SaveMethodProps) {
  const headingId = useId();
  const wordsIds = { bank: useId(), card: useId() };
  /**
   * The kind whose words ops said had changed, as the tab noted it across the load afresh, said
   * above that kind's words until she presses; read once, as the page is drawn.
   */
  const [changed, setChanged] = useState<SetupKind | undefined>(() => setupTermsChangedOn());
  const changedLine = useRef<HTMLParagraphElement>(null);
  /** The kind whose code step is drawn in place of its button, or none. */
  const [coding, setCoding] = useState<SetupKind | null>(null);
  /** The kind she pressed last, which the panel says she is going to save, and the focus returns to. */
  const [pressedKind, setPressedKind] = useState<SetupKind>('bank');
  /** The same, read at once rather than on the next draw. */
  const pressedKindNow = useRef<SetupKind>('bank');
  const buttons = {
    bank: useRef<HTMLButtonElement>(null),
    card: useRef<HTMLButtonElement>(null),
  };
  /** How you pay is read again once she closes the panel, after a method already saved. */
  const readAgainOnClose = useRef(false);
  const leaving = useLeaving<Ended>(() => {
    buttons[pressedKindNow.current].current?.focus();
    if (readAgainOnClose.current) {
      readAgainOnClose.current = false;
      reload();
    }
  });
  const { out, wait, finish, begin, waiting } = leaving;
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  const [limit, setLimit] = useState<TooMany>();
  /** The door is asked, with no fresh code standing, before any code: the button held busy. */
  const [asking, setAsking] = useState(false);
  /** The key minted for the press of a kind, kept only while the door could not be reached. */
  const minted = useRef<{ readonly kind: SetupKind; readonly key: string } | null>(null);

  // The note is the page's to say once: forgotten as it is drawn, and said with the focus.
  useEffect(() => {
    if (changed === undefined) return;
    forgetSetupTermsChanged();
    changedLine.current?.focus();
  }, []);

  function signedOut() {
    finish(null);
    signed.onSignedOut();
  }

  function refusedWith(kind: SetupKind, code: string | undefined) {
    if (code === TERMS_CHANGED_CODE) {
      // The words drawn are not the ones that hold: How you pay as the Worker serves it now draws
      // those, and says so above this kind's. The panel stays up as the page goes.
      noteSetupTermsChanged(kind);
      away.again();
      return;
    }
    if (code === ALREADY_SAVED_CODE) {
      readAgainOnClose.current = true;
      finish('already_saved');
      return;
    }
    finish(code === USED_UP_CODE ? 'used_up' : 'failed');
  }

  /** Waits on the page by her ask (`pollForPage`), and acts on what came of it. */
  async function waitForPage(kind: SetupKind, askId: string, current: () => boolean) {
    const polled = await pollForPage(() => getSetupPage(askId, household), current);
    switch (polled.outcome) {
      case 'gone':
        return;
      case 'ready':
        away.to(polled.url);
        return;
      case 'signed_out':
        signedOut();
        return;
      case 'not_found':
        finish('failed');
        return;
      case 'refused':
        refusedWith(kind, polled.code);
        return;
      case 'ended':
        finish(polled.as);
        return;
    }
  }

  /** Sends the ask for this kind's page behind the code, with the key minted for its press. */
  function send(kind: SetupKind): Promise<AskAnswer> {
    if (minted.current?.kind !== kind) minted.current = { kind, key: crypto.randomUUID() };
    return sendRequest(
      {
        key: minted.current.key,
        type: SETUP_TYPE,
        household,
        payload: { page: kind, terms_version: KINDS[kind].version },
      },
      'billing',
    );
  }

  /**
   * Acts on what the door answered. The key is let go, so the next press is a new request, unless
   * the door could not be reached or asks for a fresh code first.
   */
  async function answered(kind: SetupKind, answer: AskAnswer, current: () => boolean) {
    if (answer.outcome !== 'failed' && answer.outcome !== 'step_up') minted.current = null;
    switch (answer.outcome) {
      case 'sent':
        wait('going');
        await waitForPage(kind, answer.id, current);
        return;
      case 'step_up':
        // The door decides: no fresh code stands, whatever the tab knew of one.
        noFreshCode();
        finish(null);
        setCoding(kind);
        return;
      case 'signed_out':
        signedOut();
        return;
      case 'refused':
        refusedWith(kind, answer.code);
        return;
      case 'too_many':
        setLimit(answer.limit);
        finish('too_many');
        return;
      case 'failed':
        finish(answer.offline === true ? 'offline' : 'failed');
        return;
      default:
        finish('failed');
    }
  }

  /**
   * A button, or Try again in the panel: with no fresh code standing, no panel, the button held busy
   * while the door is asked, which asks for the code last, so it answers what it would refuse the
   * press with, said in the panel as any refusal is, or `403 step_up_required` only for a press it
   * would take, which draws the code step in place of the button (round six, slice S1); else the
   * panel at once, "One moment" until the door has the ask.
   */
  async function press(kind: SetupKind) {
    if (out.current) return;
    pressedKindNow.current = kind;
    setPressedKind(kind);
    setChanged(undefined);
    if (!freshStands()) {
      const current = begin();
      setAsking(true);
      const answer = await send(kind);
      if (!current()) return;
      setAsking(false);
      await answered(kind, answer, current);
      return;
    }
    const current = begin();
    wait('asking');
    const answer = await send(kind);
    if (!current()) return;
    await answered(kind, answer, current);
  }

  /** The code step's button is pressed: the panel at once, the one panel through what follows. */
  function codePressed(pressing: boolean) {
    if (out.current) return;
    if (pressing) wait('asking');
    else finish(null);
  }

  /** Once the fresh code works: the same ask again, with the same key, behind the code. */
  async function provedThen(kind: SetupKind): Promise<ProvedCame> {
    if (out.current) return null;
    const current = begin();
    const answer = await send(kind);
    if (!current()) return null;
    if (answer.outcome === 'step_up') {
      finish(null);
      return 'step_up';
    }
    setCoding(null);
    await answered(kind, answer, current);
    return null;
  }

  return (
    <section aria-labelledby={headingId} data-save-offer className="flex flex-col gap-2.5">
      <h2 id={headingId} className="font-serif text-h3 font-semibold text-text-body">
        {SAVE.heading}
      </h2>
      {ORDER.map((kind) => {
        const { words, button, freshCode, cannotBeforePhone } = KINDS[kind];
        return (
          <div
            key={kind}
            data-save={kind}
            className="rounded-card border border-border-subtle bg-surface-raised px-5 py-5"
          >
            {changed === kind ? (
              <p
                ref={changedLine}
                tabIndex={-1}
                className="mb-3 text-body font-semibold text-pretty text-text-body"
              >
                {TERMS_CHANGED}
              </p>
            ) : null}
            {/* The button's description: ops's words as one run of text, read with nothing between
                them, where the paragraph draws the number as a box to tap (slice A4). */}
            <span id={wordsIds[kind]} hidden>
              {words}
            </span>
            <TermsWords
              words={words}
              className="text-small text-pretty wrap-break-word text-text-body"
            />
            {coding === kind ? (
              <FreshCode
                signedInWith={signed.signedInWith}
                why={freshCode}
                submit={button}
                closedBeforePhone={cannotBeforePhone}
                onProved={() => provedThen(kind)}
                onWaiting={codePressed}
                className="mt-4"
              />
            ) : (
              <button
                ref={buttons[kind]}
                type="button"
                aria-describedby={wordsIds[kind]}
                aria-disabled={waiting || asking ? true : undefined}
                onClick={() => void press(kind)}
                className={`mt-4 cursor-pointer aria-disabled:cursor-wait aria-disabled:opacity-70 ${NAVY_BUTTON_WRAPPING}`}
              >
                {button}
              </button>
            )}
          </div>
        );
      })}
      <LeavingPanel
        leaving={leaving}
        going={KINDS[pressedKind].going}
        ended={(line) => endedWords(line, limit)}
        retry={(line) => !NO_RETRY.has(line)}
        onTryAgain={() => void press(pressedKindNow.current)}
      />
    </section>
  );
}
